import { createContext, useEffect, useMemo, useState } from "react"
import type { Context, ReactElement, ReactNode } from "react"
import { connect } from "react-redux"
import { useHistory } from "react-router-dom"
import type { Subscription } from "rxjs"
import { BehaviorSubject } from "rxjs"
import { debounceTime, skip } from "rxjs/operators"
import { AnalyticsSearchModule } from "../../../models"
import {
  changeSearchValue,
  httpGet,
  httpRequestComplete,
  performMapSearch,
  searchClearFilters,
  searchSetPreserveState,
  updateSuggestions
} from "../../../redux"
import type {
  AppState,
  AppThunkDispatch,
  HttpAction,
  MapSearchResult,
  SearchActions,
  SearchSuggestions
} from "../../../redux"
import { addSearchToHistory, isValidSearchValue } from "../../../services"

export interface ISearchBarProviderOwnProps {
  id?: string
  compactDisplay?: boolean | undefined
  disableDropdown?: boolean | undefined
  disableNavigation?: boolean | undefined
  autoFocus?: boolean | undefined
  small?: boolean | undefined
  placeholder?: string | undefined
  disableSync?: boolean
  children?: ReactNode
}

export interface ISearchBarProviderStateProps {
  searchOptions: MapSearchResult[]
  searchValue: string
  suggestions: SearchSuggestions
  setSearchValue: (searchValue: string) => void
  search: (searchValue: string, searchModule: AnalyticsSearchModule) => void
  getSuggestions: (searchValue: string) => void
  triggerLoading: (section: string) => void
  clearFilters: () => void
  completeLoading: (section: string) => void
  setPreserveSearchState: () => void
}

export interface ISearchbarContext {
  id?: string
  disableDropdown?: boolean | undefined
  disableNavigation?: boolean | undefined
  compactDisplay?: boolean | undefined
  small?: boolean | undefined
  suggestions: SearchSuggestions
  searchValue: string
  placeholder?: string
  autoFocus?: boolean | undefined
  setSearchValue: (searchValue: string) => void
  search: (searchValue?: string) => void
}

export const SearchbarContext: Context<ISearchbarContext | null> = createContext<ISearchbarContext | null>(null)

function SearchBarProviderView(props: ISearchBarProviderOwnProps & ISearchBarProviderStateProps): ReactElement {
  const {
    id,
    compactDisplay,
    disableDropdown,
    small,
    searchValue,
    disableNavigation,
    suggestions,
    placeholder,
    disableSync,
    autoFocus,
    setSearchValue,
    getSuggestions,
    search,
    triggerLoading,
    clearFilters,
    completeLoading,
    setPreserveSearchState,
    children
  } = props

  const searchDelay: number = 1000

  const [ownSearchValue, setOwnSearchValue] = useState<string>("")

  const [searchSubscription, setSearchSubscription] = useState<Subscription>()

  const [searchSubject] = useState<BehaviorSubject<string>>(
    new BehaviorSubject<string>(disableSync ? ownSearchValue : searchValue)
  )

  const searchModule: AnalyticsSearchModule = useMemo(() => {
    return small ? AnalyticsSearchModule.Widget : AnalyticsSearchModule.Main
  }, [small])

  const history = useHistory()
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  const contextValue: ISearchbarContext = useMemo<ISearchbarContext>(() => {
    return {
      id,
      compactDisplay,
      disableDropdown,
      disableNavigation,
      suggestions,
      autoFocus,
      searchValue: disableSync ? ownSearchValue : searchValue,
      setSearchValue: searchValueChange,
      search: navigateSearch,
      small,
      placeholder
    }
  }, [props, ownSearchValue])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    setSearchSubscription(
      searchSubject.pipe(skip(1), debounceTime(searchDelay)).subscribe((value: string) => {
        if (isValidSearchValue(value)) {
          if (!disableDropdown) getSuggestions(value)
          if (disableNavigation && disableDropdown && !disableSync) {
            clearFilters()
            search(value, searchModule)
          }
        }
      })
    )
    return cleanUp
  }, [])

  function searchValueChange(value: string): void {
    searchSubject.next(value)
    if (value.length > 0) {
      if (disableDropdown) {
        triggerLoading("search")
      } else {
        triggerLoading("search-suggestions")
      }
    }
    if (!value.length) {
      if (disableDropdown) {
        completeLoading("search")
      } else {
        completeLoading("search-suggestions")
      }
    }
    disableSync ? setOwnSearchValue(value) : setSearchValue(value)
  }

  function cleanUp(): void {
    if (searchSubscription) {
      searchSubscription.unsubscribe()
    }
  }

  function navigateSearch(value?: string): void {
    const searchString: string = value ? value : disableSync ? ownSearchValue : searchValue
    addSearchToHistory(searchString)
    search(searchString, searchModule)
    setSearchValue(searchString)
    setOwnSearchValue("")
    if (disableNavigation) {
      return
    }
    if (disableSync) setPreserveSearchState()
    history.push("/search")
  }

  return <SearchbarContext.Provider value={contextValue}>{children}</SearchbarContext.Provider>
}

function mapStateToProps(state: AppState, ownProps: ISearchBarProviderOwnProps) {
  return {
    disableDropdown: ownProps.disableDropdown,
    disableNavigation: ownProps.disableNavigation,
    small: ownProps.small,
    searchOptions: state.search.searchResults.items,
    searchValue: state.search.searchValue,
    suggestions: state.search.suggestions
  }
}

function mapDispatchToProps(dispatch: AppThunkDispatch<SearchActions | HttpAction>) {
  return {
    setSearchValue: (searchValue: string) => dispatch(changeSearchValue(searchValue)),
    search: (searchValue: string, searchModule: AnalyticsSearchModule) =>
      dispatch(performMapSearch(searchValue, searchModule)),
    getSuggestions: (searchValue: string) => dispatch(updateSuggestions(searchValue)),
    triggerLoading: (section: string) => dispatch(httpGet(section)),
    clearFilters: () => dispatch(searchClearFilters()),
    completeLoading: (section: string) => dispatch(httpRequestComplete(section)),
    setPreserveSearchState: () => dispatch(searchSetPreserveState())
  }
}

export const SearchBarProvider = connect(mapStateToProps, mapDispatchToProps)(SearchBarProviderView)
